"use client";

import React from 'react';
import { Layout } from 'antd';
import { useShine, type ShineConfig } from '@/shared/components';

interface ShineLayoutProps extends React.ComponentProps<typeof Layout>, ShineConfig {
  shine?: boolean;
}

const { Header, Sider, Content, Footer } = Layout;

/**
 * Layout shell primitives. Shine overlays default OFF — app shell keeps a
 * single chrome surface without animated border noise (ui-ux-pro-max).
 */
export const ShineLayout: React.FC<ShineLayoutProps> & {
  Header: React.FC<ShineHeaderProps>;
  Sider: typeof Sider;
  Content: React.FC<ShineContentProps>;
  Footer: React.FC<ShineFooterProps>;
} = ({ 
  shine = false, 
  children, 
  style, 
  // Extract shine config props (never leak onto DOM Layout)
  enabled,
  borderWidth,
  duration,
  intensity,
  paused,
  hoverBoost,
  hideBorders,
  customColors,
  themeMode,
  // Rest are Layout props
  ...layoutProps 
}) => {
  // Shell passes shine={false}; only explicit shine=true enables overlay
  const shineElement = useShine('default', { 
    enabled: shine === true && enabled !== false, 
    borderWidth,
    duration,
    intensity,
    paused,
    hoverBoost,
    hideBorders,
    customColors,
    themeMode
  });
  
  return (
    <Layout style={{ position: 'relative', overflow: 'hidden', ...style }} {...layoutProps}>
      {shineElement}
      {children}
    </Layout>
  );
};

interface ShineHeaderProps extends React.ComponentProps<typeof Header>, ShineConfig {
  shine?: boolean;
}

const ShineHeader: React.FC<ShineHeaderProps> = ({ 
  shine = false, 
  children, 
  style, 
  // Extract shine config props
  enabled,
  borderWidth,
  duration,
  intensity,
  paused,
  hoverBoost,
  hideBorders,
  customColors,
  themeMode,
  // Rest are Header props
  ...headerProps 
}) => {
  const shineElement = useShine('header', { 
    enabled: shine, 
    borderWidth,
    duration,
    intensity,
    paused,
    hoverBoost,
    hideBorders,
    customColors,
    themeMode
  });
  
  return (
    <Header style={{ position: 'relative', overflow: 'hidden', ...style }} {...headerProps}>
      {shineElement}
      {children}
    </Header>
  );
};

interface ShineContentProps extends React.ComponentProps<typeof Content>, ShineConfig {
  shine?: boolean;
}

const ShineContent: React.FC<ShineContentProps> = ({ 
  shine = false, 
  children, 
  style, 
  // Extract shine config props
  enabled,
  borderWidth,
  duration,
  intensity,
  paused,
  hoverBoost,
  hideBorders,
  customColors,
  themeMode,
  // Rest are Content props
  ...contentProps 
}) => {
  const shineElement = useShine('content', { 
    enabled: shine, 
    borderWidth,
    duration,
    intensity,
    paused,
    hoverBoost,
    hideBorders,
    customColors,
    themeMode
  });
  
  return (
    <Content style={{ position: 'relative', overflow: 'hidden', ...style }} {...contentProps}>
      {shineElement}
      {children}
    </Content>
  );
};

interface ShineFooterProps extends React.ComponentProps<typeof Footer>, ShineConfig {
  shine?: boolean;
}

const ShineFooter: React.FC<ShineFooterProps> = ({ 
  shine = false, 
  children, 
  style, 
  // Extract shine config props
  enabled,
  borderWidth,
  duration,
  intensity,
  paused,
  hoverBoost,
  hideBorders,
  customColors,
  themeMode,
  // Rest are Footer props
  ...footerProps 
}) => {
  const shineElement = useShine('footer', { 
    enabled: shine, 
    borderWidth,
    duration,
    intensity,
    paused,
    hoverBoost,
    hideBorders,
    customColors,
    themeMode
  });
  
  return (
    <Footer style={{ position: 'relative', overflow: 'hidden', ...style }} {...footerProps}>
      {shineElement}
      {children}
    </Footer>
  );
};

ShineLayout.Header = ShineHeader;
ShineLayout.Sider = Sider;
ShineLayout.Content = ShineContent;
ShineLayout.Footer = ShineFooter;

export default ShineLayout;


